/**
 * AdminPreviewBanner — floating strip that appears whenever an admin is
 * simulating a subscription tier. Visible on all screens. Provides quick
 * plan-switcher chips so you can cycle through tiers during a demo without
 * opening the admin panel.
 *
 * Admin status is NEVER affected — only the subscription view is overridden.
 */
import React from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth, ADMIN_PREVIEW_PLANS } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';

const PLAN_ORDER = ['FREE', 'BASIC', 'PRO', 'ULTIMATE'];

const PLAN_COLORS = {
  FREE:     '#6B7280',
  BASIC:    '#3B82F6',
  PRO:      '#A855F7',
  ULTIMATE: '#F59E0B',
};

export default function AdminPreviewBanner() {
  const { isAdmin } = useNavigation();
  const { adminPreviewPlan, setAdminPreviewPlan, clearAdminPreviewPlan } = useAuth();

  if (!isAdmin || !adminPreviewPlan || Platform.OS !== 'web') return null;

  const color = PLAN_COLORS[adminPreviewPlan] || '#6B7280';

  return (
    <View style={[s.strip, { borderColor: color + '60', backgroundColor: color + '18' }]}>
      {/* Left: label */}
      <View style={s.left}>
        <Ionicons name="eye-outline" size={13} color={color} />
        <Text style={[s.label, { color }]}>Previewing as</Text>
        <View style={[s.planBadge, { backgroundColor: color + '25', borderColor: color + '55' }]}>
          <Text style={[s.planBadgeText, { color }]}>{adminPreviewPlan}</Text>
        </View>
        <Text style={s.hint}>— admin status unchanged</Text>
      </View>

      {/* Center: quick-switch chips */}
      <View style={s.chips}>
        {PLAN_ORDER.map(p => {
          const active = p === adminPreviewPlan;
          const c = PLAN_COLORS[p];
          return (
            <TouchableOpacity
              key={p}
              style={[s.chip, active ? { backgroundColor: c + '30', borderColor: c } : s.chipInactive]}
              onPress={() => setAdminPreviewPlan(p)}
              activeOpacity={0.7}
            >
              <Text style={[s.chipText, { color: active ? c : 'rgba(255,255,255,0.45)' }]}>{p}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Right: exit */}
      <TouchableOpacity style={s.exitBtn} onPress={clearAdminPreviewPlan} activeOpacity={0.7}>
        <Ionicons name="close" size={14} color="rgba(255,255,255,0.6)" />
        <Text style={s.exitText}>Exit preview</Text>
      </TouchableOpacity>
    </View>
  );
}

const s = {
  strip: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 99998,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderBottomWidth: 1,
    gap: 12,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  left: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  label: { fontSize: 12, fontWeight: '600' },
  planBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 5,
    borderWidth: 1,
  },
  planBadgeText: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4 },
  hint: { fontSize: 11, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  chips: { flex: 1, flexDirection: 'row', justifyContent: 'center', gap: 6 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  chipInactive: { backgroundColor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' },
  chipText: { fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },
  exitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  exitText: { fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: '600' },
};
